import type { Except } from 'type-fest';
import type { ProductMultiVariantAddToCartFeatureOptions } from '../../../api/models/FeatureOptions';
import { Product } from '../../../models/Product';
import type { ProductMultiVariantAddToCartFeatureItem } from '../../../models/features/main/ProductMultiVariantAddToCartFeatureItem';
import type { FeatureState } from '../../../store/featureState';
import type { AppDispatch, RootState } from '../../../store/store';
import { apiClient } from '../../ApiClient';
import {
    getNewFeatureUuid,
    getDefaultBackableFeatureItem,
    defaultBackToPreviousFeatureItems
} from '../../FeatureService';
import type { FeatureHandler } from '../FeatureHandler';
import type { Feature } from '@public-api-contracts/models/chat/ChatMessage';
import type { ChatMessage } from '../../../models/ChatMessage';

export const productMultiVariantAddToCartMainFeatureHandler = {
    async startFeatureItem(
        dispatch: AppDispatch,
        state: RootState,
        featureOptions: ProductMultiVariantAddToCartFeatureOptions
    ): Promise<void> {
        dispatch.cartState.reset();
        dispatch.featureState.setMenuFeatureItem(null);
        dispatch.featureState.setMainFeatureItem({
            featureUuid: getNewFeatureUuid('product-multi-variant-add-to-cart'),
            featureType: 'product-multi-variant-add-to-cart',
            products: featureOptions.products,
            suggestedProducts: [],
            suggestedCategoryFilters: [],
            suggestedFieldFilters: [],
            ...getDefaultBackableFeatureItem(state)
        });
        dispatch.uiState.setMode('full');

        await dispatch.featureState.startFeature({
            featureType: 'chat',
            chatType: 'general'
        });
        const feature: Feature = {
            featureType: 'product-multi-variant-add-to-cart',
            request: {
                productIds: featureOptions.products.map(p => p.productId)
            },
            result: featureOptions.products
        };
        const newPartialChatMessage = {
            role: 'assistant',
            content: {
                contentType: 'feature',
                startedBy: 'user-from-ui',
                status: 'loading',
                feature
            }
        } as const satisfies Except<ChatMessage, 'timestamp' | 'uuid'>;
        dispatch.featureState.appendNewChatMessage(newPartialChatMessage);
        void dispatch.featureState.processMessages({
            handleLoading: true
        });
    },
    async loadSuggestedProducts(
        dispatch: AppDispatch,
        state: RootState,
        excludedProducts: Product[]
    ): Promise<void> {
        const mainFeatureItem = state.featureState.mainFeatureItem;
        if (
            mainFeatureItem?.featureType !== 'product-multi-variant-add-to-cart'
        ) {
            return;
        }
        const {
            jsonResult: { suggestedProducts }
        } = await apiClient.product.productGetSuggested({
            body: {
                productIds: [excludedProducts[0].productId],
                excludedProductIds: excludedProducts
                    .slice(1)
                    .map(p => p.productId)
            }
        });

        dispatch.featureState.updateMainFeatureItem({
            ...mainFeatureItem,
            suggestedProducts
        });
    },
    isBackable: true,
    backToPreviousFeatureItems(dispatch: AppDispatch, state: RootState): void {
        defaultBackToPreviousFeatureItems(dispatch, state);
    },
    removeProduct(state: FeatureState, productId: string) {
        const mainFeatureItem = state.mainFeatureItem;
        if (
            mainFeatureItem?.featureType !== 'product-multi-variant-add-to-cart'
        ) {
            return state;
        }
        const products = mainFeatureItem.products.filter(
            p => p.productId !== productId
        );
        return {
            ...state,
            mainFeatureItem: {
                ...mainFeatureItem,
                products
            }
        };
    }
} as const satisfies FeatureHandler<ProductMultiVariantAddToCartFeatureItem>;
